import { TextField } from '@refinedev/antd';
import { DatePicker, Flex, Form, Input, InputNumber, Radio } from 'antd';
import { OptionProps } from 'antd/es/select';
import { FormProps } from 'antd/lib';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RETURN_ITEM_TYPE, ReturnItemTypeString } from '@/model/Logistic/Reception/ReturnItemType';

import usePackageItemForm from './usePackageItemForm';

interface ReceptionPackageItemFormProps {
  formProps: FormProps;
  editingPackageItem?: PackageItem;
  movilLink?: string;
  createMode: boolean;
}

const ReceptionPackageItemForm = (props: ReceptionPackageItemFormProps) => {
  const { t } = useTranslation();
  const { editingPackageItem, formProps, movilLink, createMode } = props;
  const { productType, productsSelectProps, serialNumbersSelectProps } = usePackageItemForm(
    formProps,
    editingPackageItem,
  );
  const elementFormName = useMemo(() => {
    if (productType === 1) return 'unidad';
    if (productType === 2) return 'device';
  }, [productType]);
  const elementFormPlaceholder = useMemo(() => {
    if (productType === 0) return t('Seleccione un producto');
    if (productType === 1) return t('Seleccione una unidad');
    if (productType === 2) return t('Seleccione un dispositivo');

    return t('Seleccione un tipo de contenido');
  }, [productType, t]);
  // Estado de paquete
  const itemPackageStatus = useMemo(
    () =>
      typeof editingPackageItem?.package != 'number'
        ? editingPackageItem?.package?.status
        : undefined,
    [editingPackageItem],
  );
  // Form watchers
  const inWarrantyPeriod = Form.useWatch('in_warranty_period', formProps?.form);

  return (
    <>
      <fieldset>
        <legend>{t('Información del producto')}</legend>
        <Form.Item label={t('Tipo de contenido')} name="product_type">
          <AntdSelect
            options={Object.values(ProductTypeStrings).map((v, index) => ({
              label: t(v),
              value: index,
            }))}
            disabled={!createMode && !!editingPackageItem}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('Producto')}
          name={'product'}
          rules={[{ required: true, message: elementFormPlaceholder }]}>
          <AntdSelect
            {...productsSelectProps}
            disabled={productType === undefined || (!createMode && !!editingPackageItem)}
            placeholder={elementFormPlaceholder}
            onChange={(_, opt) =>
              !Array.isArray(opt) &&
              formProps?.form?.setFieldValue('name', opt?.label ?? opt?.value)
            }
            allowClear
            showSearch
          />
        </Form.Item>
        {(productType === 1 || productType === 2) && (
          <>
            <Form.Item label={t('Número de serie')} name={elementFormName}>
              <AntdSelect
                {...serialNumbersSelectProps}
                placeholder={elementFormPlaceholder}
                allowClear
                showSearch
                onChange={(v, opt) => {
                  serialNumbersSelectProps.onChange?.(v, opt);
                  formProps?.form?.setFieldValue('serial_number', (opt as OptionProps)?.label);
                }}
              />
            </Form.Item>
            <Form.Item hidden label={t('Número de serie')} name={'serial_number'}>
              <Input />
            </Form.Item>
          </>
        )}
        <Form.Item
          label={t('En período de garantía')}
          name="in_warranty_period"
          rules={[{ required: true, message: t('Seleccione estado de garantía') }]}>
          <Radio.Group>
            <Radio value={true}>{t('Sí')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        </Form.Item>
        {inWarrantyPeriod && (
          <Flex vertical style={{ marginBottom: 24 }}>
            <Form.Item
              style={{ marginBottom: 0 }}
              label={t('Fecha de inicio de garantía')}
              name="warranty_start_date"
              rules={[{ required: true, message: t('Seleccione la feceha de inicio de garantía') }]}
              getValueProps={value => ({
                value: value ? dayjs(value) : undefined,
              })}>
              <DatePicker format={'L'} placeholder={t('Seleccionar una fecha')} />
            </Form.Item>
            {movilLink && <LinkTextField label={t('Link a móvil')} to={movilLink} external />}
          </Flex>
        )}
      </fieldset>
      <fieldset>
        <legend>{t('Información de la recepción')}</legend>
        <Form.Item
          label={t('Cantidad a recibir')}
          name="requested_amount"
          rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} disabled={productType != 0} min={0} />
        </Form.Item>

        {!createMode &&
          itemPackageStatus != undefined &&
          itemPackageStatus != PACKAGE_STATES.PENDIENTE && (
            <Form.Item label={t('Cantidad recibida')} name="execute_amount">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          )}
      </fieldset>
      <Form.Item label={t('Es una devolución')}>
        <TextField value={editingPackageItem?.is_returned ? 'Sí' : 'No'} />
      </Form.Item>

      <Form.Item label={t('Motivo de devolución')}>
        <TextField
          value={
            editingPackageItem?.return_type
              ? ReturnItemTypeString[editingPackageItem.return_type]
              : ReturnItemTypeString[RETURN_ITEM_TYPE.SOLICITUD_REPARACION]
          }
        />
      </Form.Item>
    </>
  );
};

export default ReceptionPackageItemForm;
